import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Save, Wand2, Minus, Plus, Quote, Trash2, Check, History, RotateCcw, RefreshCw, ChevronUp, ChevronDown, Undo2, BookOpen as BookOpenIcon } from "lucide-react";

// Map the internal five statuses onto three plain-language customer stages.
function customerStage(status) {
  if (status === "approved") return { label: "Finished", tone: "bg-teal text-cream" };
  if (["draft", "edited"].includes(status)) return { label: "Chapter draft ready", tone: "bg-sagelight text-teal" };
  return { label: "Gathering memories", tone: "bg-cream2 text-subtle border border-line" };
}

const IMPROVE_OPTIONS = [
  { mode: "shorter", label: "Make it shorter", icon: Minus },
  { mode: "detailed", label: "Add more detail from my memories", icon: Plus },
  { mode: "original_words", label: "Keep it closer to my original words", icon: Quote },
  { mode: "standard", label: "Create a fresh version", icon: RefreshCw },
];

export default function ChapterEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [regen, setRegen] = useState("");
  const [showImprove, setShowImprove] = useState(false);
  const [prevText, setPrevText] = useState(null);
  const [orderChanged, setOrderChanged] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/chapters/${id}`);
    setChapter(data); setText(data.edited_text || data.draft_text || ""); setTitle(data.title);
  };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    setBusy(true);
    try {
      const nextStatus = chapter.status === "draft" ? "edited" : chapter.status;
      await api.put(`/chapters/${id}`, { edited_text: text, title, status: nextStatus });
      toast.success("Chapter saved");
      await load();
    } catch { toast.error("Couldn't save just now. Please try again."); }
    finally { setBusy(false); }
  };
  const improve = async (mode) => {
    const before = text;
    setRegen(mode); setShowImprove(false);
    try {
      const { data } = await api.post(`/chapters/${id}/regenerate`, { title, memory_ids: [], mode });
      setText(data.draft_text); setOrderChanged(false);
      if (before.trim()) setPrevText(before);
      toast.success("Here's a fresh version — your previous one is safe");
    } catch (e) { toast.error(e.response?.data?.detail || "We couldn't rewrite that just now. Please try again."); }
    finally { setRegen(""); }
  };
  const keepVersion = () => { setPrevText(null); toast.success("Kept this version"); };
  const revertVersion = async () => {
    if (prevText == null) return;
    setBusy(true);
    try {
      const nextStatus = chapter.status === "draft" ? "edited" : chapter.status;
      await api.put(`/chapters/${id}`, { edited_text: prevText, status: nextStatus });
      setText(prevText); setPrevText(null); toast.success("Returned to your previous version"); await load();
    } catch { toast.error("Couldn't restore just now. Please try again."); }
    finally { setBusy(false); }
  };
  const setFinished = async (finished) => {
    const status = finished ? "approved" : "edited";
    setChapter((c) => ({ ...c, status }));
    try { await api.put(`/chapters/${id}`, { status }); toast.success(finished ? "Marked as finished" : "Reopened for editing"); }
    catch { toast.error("Couldn't update just now."); await load(); }
  };
  const del = async () => {
    if (!window.confirm("Delete this chapter draft? Your memories are kept.")) return;
    await api.delete(`/chapters/${id}`); navigate("/story");
  };
  const restoreChapter = async (t) => {
    const nextStatus = chapter.status === "draft" ? "edited" : chapter.status;
    await api.put(`/chapters/${id}`, { edited_text: t, status: nextStatus });
    toast.success("Version restored"); await load();
  };
  const moveStory = async (i, dir) => {
    const mems = [...(chapter.source_memories || [])];
    const j = i + dir;
    if (j < 0 || j >= mems.length) return;
    [mems[i], mems[j]] = [mems[j], mems[i]];
    setChapter((c) => ({ ...c, source_memories: mems }));
    setOrderChanged(true);
    try { await api.put(`/chapters/${id}`, { source_memory_ids: mems.map((m) => m.id) }); }
    catch { toast.error("Couldn't reorder just now."); await load(); }
  };

  if (!chapter) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;

  const stage = customerStage(chapter.status);
  const isFinished = chapter.status === "approved";

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => navigate("/story")} className="text-subtle hover:text-charcoal text-sm">← My Book</button>
        <button data-testid="chapter-read-memoir-btn" onClick={() => navigate("/book", { state: { read: true } })}
          className="inline-flex items-center gap-2 text-teal hover:text-tealdark text-sm font-semibold">
          <BookOpenIcon size={15} /> Read my memoir so far
        </button>
      </div>

      <input data-testid="chapter-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
        className="w-full font-serif text-3xl text-charcoal bg-transparent border-b border-line focus:border-teal outline-none pb-2" />

      {/* Stage + finished toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <span data-testid="chapter-stage" className={`px-3 py-1.5 rounded-full text-xs font-semibold ${stage.tone}`}>
          {isFinished && <Check size={12} className="inline mr-1" />}{stage.label}
        </span>
        {isFinished ? (
          <button data-testid="chapter-reopen-btn" onClick={() => setFinished(false)}
            className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-4 py-1.5 rounded-full font-medium text-sm">
            Reopen for editing
          </button>
        ) : (
          <button data-testid="chapter-finish-btn" onClick={() => setFinished(true)}
            className="inline-flex items-center gap-2 bg-white border border-line hover:border-teal text-teal px-4 py-1.5 rounded-full font-medium text-sm">
            <Check size={14} /> Mark as finished
          </button>
        )}
        <span className="text-xs text-subtle">You can still edit a finished chapter whenever you like.</span>
      </div>

      <textarea data-testid="chapter-editor" value={text} onChange={(e) => setText(e.target.value)}
        className="w-full min-h-[420px] p-6 text-lg leading-relaxed rounded-2xl border border-line bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none font-serif" />

      {prevText != null && (
        <div data-testid="chapter-version-protect" className="flex flex-col sm:flex-row sm:items-center gap-2 bg-sagelight border border-sage/30 rounded-2xl px-4 py-3">
          <span className="flex-1 text-sm text-charcoal">This is a fresh version. Keep it, or return to the one you had before.</span>
          <div className="flex gap-2">
            <button data-testid="chapter-keep-version" onClick={keepVersion} className="inline-flex items-center gap-1.5 bg-teal text-cream px-4 py-2 rounded-full text-sm font-semibold"><Check size={15} /> Keep this version</button>
            <button data-testid="chapter-revert-version" onClick={revertVersion} disabled={busy} className="inline-flex items-center gap-1.5 bg-white border border-line text-charcoal px-4 py-2 rounded-full text-sm font-medium disabled:opacity-60"><Undo2 size={15} /> Return to previous</button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button data-testid="chapter-save-btn" onClick={save} disabled={busy}
          className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-6 py-2.5 rounded-full font-semibold disabled:opacity-60">
          {busy ? <><Loader2 className="animate-spin" size={16} /> Saving your chapter…</> : <><Save size={16} /> Save</>}
        </button>
        <button data-testid="chapter-improve-btn" onClick={() => setShowImprove((s) => !s)} disabled={!!regen}
          className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-medium text-sm disabled:opacity-60">
          {regen ? <><Loader2 className="animate-spin" size={15} /> Creating your chapter…</> : <><Wand2 size={15} /> Help me improve this chapter</>}
        </button>
      </div>

      {showImprove && !regen && (
        <div data-testid="improve-panel" className="bg-white rounded-2xl border border-line p-5 animate-fade-up">
          <p className="text-sm text-charcoal/80 mb-3">How would you like to improve it?</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {IMPROVE_OPTIONS.map((o) => (
              <button key={o.mode} data-testid={`improve-${o.mode}`} onClick={() => improve(o.mode)}
                className="inline-flex items-center gap-2 bg-cream2 hover:bg-sagelight text-charcoal px-4 py-3 rounded-xl font-medium text-sm text-left transition-colors">
                <o.icon size={16} className="text-teal shrink-0" /> {o.label}
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-subtle leading-relaxed">
            We'll only use the memories you have provided. We won't invent events or facts, and you can return to your previous version.
          </p>
        </div>
      )}

      {/* Source memories — reorder the stories that make up this chapter */}
      <div className="bg-cream2 rounded-2xl p-6">
        <h3 className="font-serif text-lg text-charcoal">The stories in this chapter</h3>
        <p className="text-subtle text-sm mt-1">Built only from these memories — nothing invented. Use the arrows to set their order, then create a fresh version to re-weave the chapter in that order.</p>
        {orderChanged && (
          <div data-testid="reorder-nudge" className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 bg-coral/10 border border-coral/20 rounded-xl px-4 py-3">
            <span className="flex-1 text-sm text-charcoal">You've changed the order. Create a fresh version to weave the chapter in the new order.</span>
            <button data-testid="reorder-regen-btn" onClick={() => improve("standard")} disabled={!!regen}
              className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-4 py-2 rounded-full font-semibold text-sm disabled:opacity-60">
              {regen ? <><Loader2 className="animate-spin" size={15} /> Creating…</> : <><RefreshCw size={15} /> Create a fresh version</>}
            </button>
          </div>
        )}
        <div className="mt-4 space-y-2">
          {(chapter.source_memories || []).map((m, i) => (
            <div key={m.id} data-testid={`source-mem-${m.id}`} className="flex items-center gap-3 bg-white rounded-xl border border-line p-3">
              {(chapter.source_memories || []).length > 1 && (
                <div className="flex flex-col">
                  <button data-testid={`story-up-${i}`} onClick={() => moveStory(i, -1)} disabled={i === 0}
                    className="text-subtle hover:text-teal disabled:opacity-30 transition-colors" title="Move up"><ChevronUp size={16} /></button>
                  <button data-testid={`story-down-${i}`} onClick={() => moveStory(i, 1)} disabled={i === chapter.source_memories.length - 1}
                    className="text-subtle hover:text-teal disabled:opacity-30 transition-colors" title="Move down"><ChevronDown size={16} /></button>
                </div>
              )}
              <button onClick={() => navigate(`/memories/${m.id}`)} className="flex-1 text-left">
                <p className="text-sm font-medium text-teal">{m.question_text}</p>
                <p className="text-sm text-subtle line-clamp-1">{m.original_transcript}</p>
              </button>
            </div>
          ))}
        </div>
      </div>

      {chapter.versions?.length > 0 && (
        <div data-testid="chapter-versions" className="bg-white rounded-2xl border border-line p-6">
          <h3 className="font-serif text-lg text-charcoal flex items-center gap-2"><History size={18} /> Earlier versions</h3>
          <p className="text-subtle text-sm mt-0.5">Nothing is ever lost — restore a previous draft at any time.</p>
          <div className="mt-3 space-y-2">
            {[...chapter.versions].reverse().map((v, i) => (
              <div key={v.created_at || `${(v.text || "").slice(0, 24)}-${i}`} data-testid={`chapter-version-${i}`} className="bg-cream2 border border-line rounded-xl p-4">
                <p className="text-sm text-charcoal/80 line-clamp-3 leading-relaxed">{v.text}</p>
                <button data-testid={`restore-chapter-version-${i}`} onClick={() => restoreChapter(v.text)}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-teal hover:gap-2.5 transition-all">
                  <RotateCcw size={14} /> Restore this version
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button data-testid="chapter-delete-btn" onClick={del} className="inline-flex items-center gap-2 text-destructive/80 hover:text-destructive text-sm">
        <Trash2 size={15} /> Delete draft
      </button>
    </div>
  );
}
